import { useQuery } from "@tanstack/react-query";

/**
 * One row exactly as `GET /api/feature-flags` returns it.
 *
 * 🔴 `rollout_pct` IS NOT OPTIONAL DECORATION — it is half the flag. The column
 * is `integer NOT NULL DEFAULT 100` (admin-phase2.sql), the admin console
 * offers a slider for it and paints an amber "Partial rollout" badge below 100,
 * and the route that serves this row says in its own comment that "per-user
 * rollout gating is applied on the client using isFlagEnabledForUser()".
 * Modelling the row as `{ key, value }` did not merely ignore the column: it
 * turned a 10% canary into a 100% release on web, because `value` is `true` for
 * the whole rollout and the percentage is the only thing that says who is in it.
 *
 * `null`/absent means 100, matching the column default and mobile's
 * resolveFeatureFlags.
 */
interface FlagRow {
  key: string;
  value: boolean;
  rollout_pct?: number | null;
}

interface FlagResponse {
  flags: FlagRow[];
  cacheBustVersion: number;
}

export interface FeatureFlags {
  coin_flip_enabled: boolean;
  /** Gates the seller-facing Import Hub. Server-owned; admin-toggleable. */
  import_hub_enabled: boolean;
  /** Gates the AI photo scan. Server-owned; admin-toggleable. */
  scan_enabled: boolean;
  [key: string]: boolean;
}

/**
 * ⚠️ FAIL CLOSED, AND NAMED RATHER THAN LEFT TO THE INDEX SIGNATURE.
 *
 * `[key: string]: boolean` types every unknown key as `boolean`, so
 * `flags.import_hub_enabled` type-checks as a real boolean while being
 * `undefined` at runtime whenever the fetch fails or the server omits the row.
 * `undefined` is falsy, so the gate happened to close — but it closed by
 * accident, and a reader had no way to see that from the type. Listing the two
 * gates here makes the default an explicit `false` that the compiler can see.
 */
const DEFAULT_FLAGS: FeatureFlags = {
  coin_flip_enabled: false,
  import_hub_enabled: false,
  scan_enabled: false,
};

/**
 * The bucket a signed-out visitor falls in.
 *
 * The SAME literal mobile uses (subscribeToFeatureFlags), deliberately: the
 * bucket is `hash(userId + key) % 100`, so a different placeholder here would
 * put the two platforms' logged-out visitors in different buckets for the same
 * flag and make a partial rollout mean two things.
 */
export const ANONYMOUS_ROLLOUT_ID = "__anonymous__";

/**
 * Deterministic per-user rollout gate — a port of mobile's
 * `isFlagEnabledForUser` (artifacts/mobile/lib/featureFlags.ts), djb2 hash and
 * all, so the same user lands in the same bucket on both platforms.
 *
 * Ported rather than hoisted into a shared package for the reason
 * lib/importHub.ts's header sets out at length: adding a workspace package
 * rewrites `pnpm-lock.yaml`, which is a documented EAS build breaker here.
 * featureFlagRollout.test.ts recomputes the hash from the published rule rather
 * than from this function, and anchors the constants against mobile's source,
 * so the copy cannot quietly drift.
 *
 * `enabled` is `row.value` — untyped JSON off the wire with nothing validating
 * it. A string "false", the number 1 and an empty object are all truthy in JS
 * and would all resolve a flag ON under `if (!enabled)`, so this requires the
 * actual boolean `true`.
 */
export function isFlagEnabledForUser(
  key: string,
  userId: string,
  rolloutPct: number,
  enabled: boolean,
): boolean {
  if (enabled !== true) return false;
  if (rolloutPct >= 100) return true;
  if (rolloutPct <= 0) return false;
  const str = userId + key;
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  }
  return (h % 100) < rolloutPct;
}

/**
 * Raw rows → the resolved flag map for one user.
 *
 * Pure and exported so the gating is testable without a query client, and so
 * the wiring test can prove the hook actually reaches it with a real user id
 * rather than merely being able to.
 */
export function resolveFlags(rows: FlagRow[], userId: string): FeatureFlags {
  const flags: FeatureFlags = { ...DEFAULT_FLAGS };
  for (const row of rows) {
    const pct = row.rollout_pct == null ? 100 : row.rollout_pct;
    flags[row.key] = isFlagEnabledForUser(row.key, userId, pct, row.value);
  }
  return flags;
}

/**
 * The flags, resolved for a given seller.
 *
 * `userId` is a parameter rather than something read from AuthContext here on
 * purpose: this module is imported by tests and by surfaces that render outside
 * the provider, and AuthContext constructs a Supabase client at import time.
 * The seller-facing gate that DOES need the id supplies it — see
 * use-import-hub-gate.ts, which reads it from `useAuth` so no caller has to
 * remember to.
 *
 * Omitting it resolves against ANONYMOUS_ROLLOUT_ID, which still honours the
 * two ends that matter everywhere: 0% is off for everyone and 100% is on for
 * everyone. Only a partial rollout needs to know who is asking.
 */
export function useFeatureFlags(userId?: string | null): FeatureFlags {
  const { data } = useQuery<FlagResponse>({
    queryKey: ["feature-flags"],
    queryFn: async () => {
      const res = await fetch("/api/feature-flags");
      if (!res.ok) return { flags: [], cacheBustVersion: 0 };
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  if (!data) return DEFAULT_FLAGS;

  return resolveFlags(data.flags, userId || ANONYMOUS_ROLLOUT_ID);
}
